/**
 * 语言检测工具函数
 * @author NUXT 技术专家
 * @created 2024/12/19
 * @description 基于 host 判断语言类型的公共函数
 */

/** 英文站点的域名匹配规则（不含端口） */
export const ENGLISH_HOSTS = [
  'en.jufair.com',
  'dev-en.jufair.com',
  'pre-pc-en.jufair.com',
  'vue-en.jufair.com'
] as const

/** 中文站点的域名匹配规则（不含端口） */
export const CHINESE_HOSTS = [
  'www.jufair.com',
  'jufair.com',
  'cn.jufair.com',
  'vue.jufair.com',
  'mvue.jufair.com',
  'mmvue.jufair.com',
  'mmmvue.jufair.com',
  'pre-pc.jufair.com',
  'localhost',
  '127.0.0.1',
  'visa-dev.jufair.com',
  'visa.jufair.com'
] as const

/**
 * 中英文站点 origin 对照（按环境配对）
 * - 本地：localhost:7001 ↔ http://dev-en.jufair.com:7001
 * - 测试：vue.jufair.com ↔ https://vue-en.jufair.com
 * - 预发：pre-pc.jufair.com ↔ https://pre-pc-en.jufair.com
 * - 生产：www.jufair.com ↔ https://en.jufair.com
 */
const SITE_ORIGIN_PAIRS: Array<{ cn: string; en: string; hosts: string[] }> = [
  {
    cn: 'http://localhost:7001',
    en: 'http://dev-en.jufair.com:7001',
    hosts: ['localhost', '127.0.0.1', 'dev-en.jufair.com']
  },
  {
    cn: 'https://vue.jufair.com',
    en: 'https://vue-en.jufair.com',
    hosts: ['vue.jufair.com', 'vue-en.jufair.com']
  },
  {
    cn: 'https://pre-pc.jufair.com',
    en: 'https://pre-pc-en.jufair.com',
    hosts: ['pre-pc.jufair.com', 'pre-pc-en.jufair.com']
  },
  {
    cn: 'https://www.jufair.com',
    en: 'https://en.jufair.com',
    hosts: ['www.jufair.com', 'jufair.com', 'cn.jufair.com', 'en.jufair.com']
  }
]

function normalizeHostname(host?: string): string {
  if (!host) return ''
  return host.split(':')[0].toLowerCase()
}

/**
 * 根据 host 判断语言类型
 * @param host 主机名
 * @returns 'cn' | 'en'
 */
export function getLanguageByHost(host?: string): 'cn' | 'en' {
  const hostname = normalizeHostname(host)

  if (!hostname) {
    // 开发环境下输出警告
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Language Detection] 未获取到 host，默认返回中文')
    }
    return 'cn'
  }

  // 精确匹配
  if ((ENGLISH_HOSTS as readonly string[]).includes(hostname)) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Language Detection] 检测到英文站点: ${hostname} -> en`)
    }
    return 'en'
  }

  if ((CHINESE_HOSTS as readonly string[]).includes(hostname)) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Language Detection] 检测到中文站点: ${hostname} -> cn`)
    }
    return 'cn'
  }

  // 默认返回中文
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Language Detection] 未识别的域名: ${hostname}，默认返回中文`)
  }
  return 'cn'
}

/**
 * 根据当前访问 host，解析目标语言站点的 origin（无尾斜杠）
 * @param targetLang 目标语言：'zh' | 'en' | 'cn'
 * @param host 当前 host（可带端口），默认取浏览器 location.host
 */
export function resolveSiteOriginByLang(
  targetLang: 'zh' | 'en' | 'cn',
  host?: string
): string | null {
  const lang = targetLang === 'zh' || targetLang === 'cn' ? 'cn' : 'en'
  const currentHost = host || (typeof window !== 'undefined' ? window.location.host : '')
  const hostname = normalizeHostname(currentHost)
  if (!hostname) return null

  const pair = SITE_ORIGIN_PAIRS.find(item => item.hosts.includes(hostname))
  if (pair) {
    return lang === 'en' ? pair.en : pair.cn
  }

  return null
}

/**
 * 检查是否为英文站点
 * @param host 主机名
 * @returns boolean
 */
export function isEnglishSite(host?: string): boolean {
  return getLanguageByHost(host) === 'en'
}

/**
 * 检查是否为中文站点
 * @param host 主机名
 * @returns boolean
 */
export function isChineseSite(host?: string): boolean {
  return getLanguageByHost(host) === 'cn'
}
